import { cn } from "@wa/ui";
import type { Lead, LeadStatus } from "../../../lib/clients";
import { ScrollFade } from "../_shared/ScrollFade";
import { LeadCard } from "./LeadCard";
import { STATUSES, type T } from "./shared";

/**
 * The board: one column per stage, every lead as a card in the column it is in.
 *
 * The stage is the whole point of this view - it is read by WHERE a card sits,
 * not by a word on it - which is also why the board keeps its own empty note per
 * column instead of one for the page. An empty column says "nothing is waiting
 * at this stage", and that is a different fact from "there are no leads".
 */
export function LeadsBoard({
  leads,
  t,
  onUpdate,
  onDelete,
  onConvert,
  onPlanStep,
}: {
  leads: Lead[];
  t: T;
  onUpdate: (id: string, status: LeadStatus) => void;
  onDelete: (id: string) => void;
  onConvert: (id: string) => void;
  onPlanStep: (lead: Lead) => void;
}) {
  return (
    <ScrollFade className="pb-4" deps={[leads]}>
      <div className="flex min-w-max gap-3">
        {STATUSES.map(({ key, color }) => {
          const items = leads.filter((l) => l.status === key);
          return (
            <div key={key} className={cn("flex w-60 flex-col rounded-xl border border-border p-2", color)}>
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(`leads.status.${key}`)}
                </span>
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {items.length}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {items.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    t={t}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onConvert={onConvert}
                    onPlanStep={() => onPlanStep(lead)}
                  />
                ))}
                {items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                    {t("leads.empty")}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollFade>
  );
}
